import { useMemo } from 'react';
import { CampaignCreateDto, CampaignUpdateDto } from '@libs/dto';
import { campaignApi, emptyResponse } from '@carcareers/store';
import { useConfirmation, useLoading, useSnackbar } from '@carcareers/contexts';
import { isError, getErrorsAsString, getErrors } from '@carcareers/utils';
import { Campaign } from '@libs/models';
import { ApiError, ApiResponse } from '@libs/types';

interface CampaignCallbacks {
  onSuccess?: () => void;
}

export const useCampaign = () => {
  const { setLoading, setLabel } = useLoading();
  const { confirm } = useConfirmation();
  const { toggleSnackbar } = useSnackbar();
  const [campaignCreate] = campaignApi.useCampaignCreateMutation();
  const [campaignUpdate] = campaignApi.useCampaignUpdateMutation();
  const [campaignDelete] = campaignApi.useCampaignDeleteMutation();

  return useMemo(
    () => ({
      createCampaign: async (
        campaign?: CampaignCreateDto, 
        callbacks?: CampaignCallbacks
      ): Promise<ApiResponse<Campaign>> => {
        if (!campaign) {
          toggleSnackbar('warning', 'Campaign data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Creating campaign...');
        setLoading(true);
        const response = await campaignCreate(campaign);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Campaign created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          if (errors.length !== 0) {
            toggleSnackbar('error', getErrorsAsString(response));
          }
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
      updateCampaign: async (
        campaign?: CampaignUpdateDto, 
        callbacks?: CampaignCallbacks
      ): Promise<ApiResponse<Campaign>> => {
        if (!campaign) {
          toggleSnackbar('warning', 'Campaign data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating campaign...');
        setLoading(true);
        const response = await campaignUpdate(campaign);
        let errors: ApiError[] = [];
        
        if (!isError(response)) {
          toggleSnackbar('success', 'Campaign updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
      deleteCampaign: async (
        id?: number, 
        callbacks?: CampaignCallbacks
      ): Promise<ApiResponse<Campaign>> => {
        if (!id) {
          return { data: undefined, errors: undefined };
        }

        const confirmed = await confirm({
          title: 'Delete Campaign',
          message: 'Are you sure you want to delete this campaign?',
        });

        if (!confirmed) return { data: undefined, errors: undefined };

        setLabel('Deleting campaign...');
        setLoading(true);
        const response = await campaignDelete(id);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', `Deleted campaign ${id}`);
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);
        return {
          errors,
          data: response.data,
        };
      },
      findByPost: (postId?: number) =>
        postId ? campaignApi.useCampaignFindByPostQuery({ postId, page: 1, limit: 10 }) : emptyResponse,
    }),
    [campaignCreate, campaignUpdate, campaignDelete, setLoading, setLabel, confirm, toggleSnackbar],
  );
};
